import Link from "next/link";
import React, { ReactNode } from "react";

import BreadCrumb from "shared/BreadCrumb";
import { AppLayout } from "shared/Layout";
import { fetchSettings } from "shared/SettingsProvider/settings.helper";
import { BestSellersCatalog } from "shared/catalog/BestSellersCatalog";
import { BestSellersContextProvider } from "shared/catalog/BestSellersCatalog/BestSellersContext";
import { getBestSellerEntries } from "shared/catalog/BestSellersCatalog/best-sellers-catalog.helper";
import { Button } from "ui/Button";
import { withApp } from "utils/with-app.utils";
import { fetchTranslations, useI18n } from "utils/with-i18n.utils";
import { fetchSEO, withSeo } from "utils/with-seo.utils";

import { Banner } from "../Banner";
import { ImgDirection, TextBlockWithImg } from "../TextBlockWithImg";

import { Reconditionning } from "./Reconditionning";

export type SmartphonesReconditionnesProps = Awaited<
  ReturnType<typeof getStaticProps>
>["props"];

export const SmartphonesReconditionnes = ({
  bestSellerEntries,
}: SmartphonesReconditionnesProps) => {
  const i18n = useI18n();

  return (
    <>
      <div className="mx-auto max-w-[1440px]">
        <BreadCrumb
          parts={[
            { label: i18n.t("breadcrumb.home"), to: "/" },
            {
              label: i18n.t("breadcrumb.reconditionedSmartphones"),
              to: "/smartphone-reconditionnes",
            },
          ]}
        />

        <div className="flex flex-col px-5 xl:px-32">
          <Banner
            firstLine={i18n.t("reconditionedSmartphones.title.firstPart")}
            secondLine={i18n.t("reconditionedSmartphones.title.secondPart")}
          />

          <section className="py-5 px-4 mb-[60px] text-xl text-center rounded-xl md:px-[108px] lg:text-2xl bg-yellow">
            {i18n.t("reconditionedSmartphones.description", {}, { html: true })}
          </section>

          <TextBlockWithImg
            title={i18n.t(
              "reconditionedSmartphones.firstTitle",
              {},
              { html: true },
            )}
            description={i18n.t(
              "reconditionedSmartphones.firstParagraph",
              {},
              { html: true },
            )}
            imgSrc="/images/seo/smartphones-reconditionnes/firstPic.png"
            imgDirection={ImgDirection.Right}
            priority={true}
          />

          <Reconditionning />

          <div className="flex flex-col gap-12 mb-[60px] lg:gap-16">
            <h2 className="text-2xl text-center md:text-3xl">
              {i18n.t("reconditionedSmartphones.benefits", {}, { html: true })}
            </h2>
            <TextBlockWithImg
              title={i18n.t(
                "reconditionedSmartphones.secondTitle",
                {},
                { html: true },
              )}
              description={i18n.t(
                "reconditionedSmartphones.secondParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/smartphones-reconditionnes/secondPic.png"
              imgDirection={ImgDirection.Right}
            />
            <TextBlockWithImg
              title={i18n.t(
                "reconditionedSmartphones.thirdTitle",
                {},
                { html: true },
              )}
              description={i18n.t(
                "reconditionedSmartphones.thirdParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/smartphones-reconditionnes/thirdPic.png"
              imgDirection={ImgDirection.Left}
            />
            <TextBlockWithImg
              title={i18n.t(
                "reconditionedSmartphones.fourthTitle",
                {},
                { html: true },
              )}
              description={i18n.t(
                "reconditionedSmartphones.fourthParagraph",
                {},
                { html: true },
              )}
              imgSrc="/images/seo/smartphones-reconditionnes/fourthPic.png"
              imgDirection={ImgDirection.Right}
            />
          </div>

          <Link href="/smartphone">
            <a className="flex justify-center items-center">
              <Button>
                {i18n.t("reconditionedSmartphones.button")}
              </Button>
            </a>
          </Link>
        </div>
      </div>

      <BestSellersContextProvider entries={bestSellerEntries}>
        <div className="flex flex-col items-center py-12">
          <h2 className="text-4xl font-medium text-center lg:text-6xl">
            {i18n.t("reconditionedSmartphones.bestseller.title")}
          </h2>

          <div className="flex flex-wrap gap-4 justify-center px-5 my-8 w-full max-w-6xl lg:my-12">
            <BestSellersCatalog />
          </div>
        </div>
      </BestSellersContextProvider>
    </>
  );
};

SmartphonesReconditionnes.getLayout = (
  page: ReactNode,
  pageProps: SmartphonesReconditionnesProps,
) => (
  <AppLayout withBanner {...pageProps}>
    {page}
  </AppLayout>
);

export const getStaticProps = async () => {
  const translations = await fetchTranslations([
    "marquee",
    "breadcrumb",
    "reconditioned-smartphones",
    "catalog",
  ]);

  const seo = await fetchSEO("smartphones-reconditionnes");

  return {
    props: {
      translations,
      seo,
      settings: await fetchSettings(),
      bestSellerEntries: await getBestSellerEntries(),
    },
  };
};

export default withApp(SmartphonesReconditionnes, [withSeo]);
